import { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';

import { Box, TextField as MuiTextField, Typography } from '@mui/material';
import { LockReset } from '@mui/icons-material';
import styled from '@emotion/styled';
import { spacing } from '@mui/system';
import { Formik } from 'formik';
import * as Yup from 'yup';

import Center from '../../components/common/Center';
import GuestGuard from '../../components/guards/GuestGuard';
import MessageAlert from '../../components/common/MessageAlert';
import SubmitButton from '../../components/common/SubmitButton';

import { useAuthStore, useUserMessageStore } from '../../stores';
import { requestPasswordReset } from '../../apis/authApi';

const TextField = styled(MuiTextField)(spacing);
const Button = styled(SubmitButton)(spacing);

const LockIcon = styled(LockReset)`
  width: 60px;
  height: 60px;
  margin: 0 auto ${(props) => props.theme.spacing(5)};
`;

function PasswordResetRequest() {
  const signOutLocal = useAuthStore.use.signOutLocal();
  const setUserMessage = useUserMessageStore.use.setUserMessage();
  const clearMessages = useUserMessageStore.use.clearMessages();

  useEffect(() => {
    clearMessages();
    signOutLocal();
  }, []);

  return (
    <>
      <GuestGuard>
        <Helmet title="Password Reset" />
        <Center>
          <LockIcon color="primary" />
        </Center>
        <Typography component="h1" variant="h3" align="center" gutterBottom>
          Reset Password
        </Typography>
        <Typography component="h2" variant="subtitle1" align="center">
          Send a reset link to your registered email
        </Typography>
        <Box mt={4}>
          <Formik
            initialValues={{
              email: '',
              submit: false,
            }}
            validationSchema={Yup.object().shape({
              email: Yup.string().email('Must be a valid email').max(255).required('Email is required'),
            })}
            onSubmit={async (values, { setErrors, setStatus, setSubmitting }) => {
              try {
                await requestPasswordReset({ email: values.email });
                setStatus({ success: true });
                setUserMessage("Great! If your email is valid, we'll send you a reset link.", 'success');
              } catch (error: any) {
                const message = error.message || 'Something went wrong';
                setStatus({ success: false });
                setErrors({ submit: message });
              } finally {
                setSubmitting(false);
              }
            }}
          >
            {({ errors, handleBlur, handleChange, handleSubmit, isSubmitting, touched, values }) => (
              <form noValidate onSubmit={handleSubmit}>
                <MessageAlert></MessageAlert>
                <TextField
                  type="email"
                  name="email"
                  autoComplete="email"
                  autoFocus={true}
                  label="Email Address"
                  value={values.email}
                  error={Boolean(touched.email && errors.email)}
                  fullWidth
                  helperText={touched.email && errors.email}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  my={5}
                />
                <Button my={1} text="Send reset link" isSubmitting={isSubmitting}></Button>
              </form>
            )}
          </Formik>
        </Box>
      </GuestGuard>
    </>
  );
}

export default PasswordResetRequest;
